// Shared helpers for the PB Intelligence Layer (/intelligence).
// Reuses the Altveno design tokens from pulse.ts.
import { C } from './pulse';

export { C };

// ── Types ────────────────────────────────────────────────────────────────────
export interface ActivityRow {
  id: string;
  profile_id: string | null;
  firm_id: string | null;
  action_type: string;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, any>;
  created_at: string;
}

export interface AlertRule {
  id: string;
  created_by: string | null;
  alert_type: AlertType;
  firm_id: string | null;
  threshold_value: number | null;
  is_active: boolean;
  last_triggered_at: string | null;
  created_at: string;
}

export interface AlertTrigger {
  id: string;
  alert_id: string | null;
  firm_id: string | null;
  profile_id: string | null;
  message: string;
  is_reviewed: boolean;
  triggered_at: string;
}

export interface ReportNotes {
  id?: string;
  created_by?: string | null;
  firm_id: string;
  period_start: string;
  period_end: string;
  executive_summary: string | null;
  altconnect_notes: string | null;
  event_notes: string | null;
  talent_notes: string | null;
  service_notes: string | null;
  ai_notes: string | null;
}

export type AlertType = 'firm_inactive' | 'first_investment' | 'connection_milestone' | 'event_low_attendance';

export const ALERT_TYPES: { value: AlertType; label: string; thresholdLabel: string; thresholdHint: string; hasThreshold: boolean }[] = [
  { value: 'firm_inactive', label: 'Firm inactive for X days', thresholdLabel: 'Days inactive', thresholdHint: 'e.g. 30', hasThreshold: true },
  { value: 'first_investment', label: 'First investment made via platform', thresholdLabel: '', thresholdHint: '', hasThreshold: false },
  { value: 'connection_milestone', label: 'Firm reaches X connections', thresholdLabel: 'Connection count', thresholdHint: 'e.g. 10', hasThreshold: true },
  { value: 'event_low_attendance', label: 'Event attendance below X%', thresholdLabel: 'Fill rate %', thresholdHint: 'e.g. 50', hasThreshold: true },
];

export const alertTypeLabel = (t: string) => ALERT_TYPES.find(a => a.value === t)?.label || t;

// ── Date ranges ──────────────────────────────────────────────────────────────
export type RangePreset = 'this_month' | 'last_month' | 'this_quarter' | 'last_quarter' | 'ytd' | 'custom';

export interface DateRange { start: Date; end: Date; }

export const RANGE_PRESETS: { value: RangePreset; label: string }[] = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'ytd', label: 'YTD' },
  { value: 'custom', label: 'Custom' },
];

const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d: Date) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

// Returns {start, end} for a preset relative to `now`.
export function rangeForPreset(preset: RangePreset, now: Date = new Date()): DateRange {
  const y = now.getFullYear();
  const m = now.getMonth();
  const q = Math.floor(m / 3);
  switch (preset) {
    case 'this_month':
      return { start: startOfDay(new Date(y, m, 1)), end: endOfDay(now) };
    case 'last_month':
      return { start: startOfDay(new Date(y, m - 1, 1)), end: endOfDay(new Date(y, m, 0)) };
    case 'this_quarter':
      return { start: startOfDay(new Date(y, q * 3, 1)), end: endOfDay(now) };
    case 'last_quarter': {
      const lqStartMonth = q * 3 - 3;
      return { start: startOfDay(new Date(y, lqStartMonth, 1)), end: endOfDay(new Date(y, lqStartMonth + 3, 0)) };
    }
    case 'ytd':
      return { start: startOfDay(new Date(y, 0, 1)), end: endOfDay(now) };
    default:
      return { start: startOfDay(new Date(y, m, 1)), end: endOfDay(now) };
  }
}

// The immediately-preceding window of equal length, for trend comparisons.
export function previousRange(r: DateRange): DateRange {
  const len = r.end.getTime() - r.start.getTime();
  return { start: new Date(r.start.getTime() - len - 1), end: new Date(r.start.getTime() - 1) };
}

export const toISO = (d: Date) => d.toISOString();
export const toDateInput = (d: Date) => d.toISOString().slice(0, 10);
export const fromDateInput = (s: string, end = false) => end ? endOfDay(new Date(s + 'T00:00:00')) : startOfDay(new Date(s + 'T00:00:00'));

// ── Formatting ───────────────────────────────────────────────────────────────
export function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return '-';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function fmtDateTime(d: string | Date | null | undefined): string {
  if (!d) return '-';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function daysSince(d: string | Date | null | undefined, now: Date = new Date()): number | null {
  if (!d) return null;
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date.getTime())) return null;
  return Math.floor((now.getTime() - date.getTime()) / 86400000);
}

export function fmtRelative(d: string | Date | null | undefined): string {
  const days = daysSince(d);
  if (days === null) return 'never';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export function fmtNumber(n: number): string {
  return n.toLocaleString('en-GB');
}

export function fmtAUM(min?: number | null, max?: number | null, exact?: number | null): string {
  const b = (v: number) => v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(0)}M` : `$${v.toLocaleString()}`;
  if (exact) return b(exact);
  if (min && max) return `${b(min)}–${b(max)}`;
  if (min) return `${b(min)}+`;
  return '-';
}

// ── Action-type presentation ─────────────────────────────────────────────────
// icon is a short glyph; colour keys off the semantic palette.
export interface ActionMeta { label: string; icon: string; color: string; category: ActionCategory; }
export type ActionCategory = 'connections' | 'messages' | 'events' | 'ai' | 'talent' | 'service' | 'ddq' | 'documents' | 'survey' | 'pipeline';

export const ACTION_META: Record<string, ActionMeta> = {
  connection_requested:   { label: 'Connection requested', icon: '⇄', color: C.navy,    category: 'connections' },
  connection_approved:    { label: 'Connection approved',  icon: '✓', color: C.success, category: 'connections' },
  pipeline_stage_updated: { label: 'Pipeline stage advanced', icon: '↗', color: C.teal, category: 'pipeline' },
  message_sent:           { label: 'Message sent',         icon: '✉', color: C.muted,   category: 'messages' },
  ddq_submitted:          { label: 'DDQ submitted',        icon: '▤', color: C.amber,   category: 'ddq' },
  document_uploaded:      { label: 'Document uploaded',    icon: '↑', color: C.teal,    category: 'documents' },
  document_accessed:      { label: 'Document accessed',    icon: '◉', color: C.mutedLt, category: 'documents' },
  event_registered:       { label: 'Event registered',    icon: '◆', color: C.gold,    category: 'events' },
  talent_connection_made: { label: 'Talent connection',   icon: '◈', color: C.teal,    category: 'talent' },
  job_posted:             { label: 'Job posted',          icon: '▸', color: C.navy,    category: 'talent' },
  service_connection_made:{ label: 'Service connection',   icon: '◇', color: C.teal,    category: 'service' },
  survey_completed:       { label: 'Survey completed',    icon: '✓', color: C.success, category: 'survey' },
  ai_query_made:          { label: 'AI query',            icon: '✧', color: C.gold,    category: 'ai' },
};

export const actionMeta = (t: string): ActionMeta =>
  ACTION_META[t] || { label: t.replace(/_/g, ' '), icon: '•', color: C.muted, category: 'connections' };

// Categories tracked on the platform activity chart (Overview tab).
export const CHART_CATEGORIES: { key: ActionCategory; label: string; color: string; actions: string[] }[] = [
  { key: 'connections', label: 'Connections', color: C.navy,    actions: ['connection_requested', 'connection_approved'] },
  { key: 'messages',    label: 'Messages',    color: C.teal,    actions: ['message_sent'] },
  { key: 'events',      label: 'Events',      color: C.gold,    actions: ['event_registered'] },
  { key: 'ai',          label: 'AI Queries',  color: C.success, actions: ['ai_query_made'] },
];

// Bucket activity rows by day (YYYY-MM-DD) across a set of categories.
export function bucketByDay(rows: ActivityRow[], range: DateRange): { date: string; label: string; [k: string]: any }[] {
  const dayMs = 86400000;
  const days = Math.max(1, Math.ceil((range.end.getTime() - range.start.getTime()) / dayMs));
  // For long ranges, bucket by week to keep the chart legible.
  const byWeek = days > 70;
  const bucketMs = byWeek ? dayMs * 7 : dayMs;
  const buckets: Record<string, any> = {};
  const order: string[] = [];
  for (let t = startOfDay(range.start).getTime(); t <= range.end.getTime(); t += bucketMs) {
    const d = new Date(t);
    const key = d.toISOString().slice(0, 10);
    order.push(key);
    buckets[key] = {
      date: key,
      label: byWeek
        ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
        : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      connections: 0, messages: 0, events: 0, ai: 0,
    };
  }
  const findBucketKey = (ts: number): string | null => {
    const idx = Math.floor((startOfDay(new Date(ts)).getTime() - startOfDay(range.start).getTime()) / bucketMs);
    return order[idx] || null;
  };
  rows.forEach(r => {
    const cat = actionMeta(r.action_type).category;
    const catKey = CHART_CATEGORIES.find(c => c.actions.includes(r.action_type))?.key;
    if (!catKey) return;
    const key = findBucketKey(new Date(r.created_at).getTime());
    if (key && buckets[key]) buckets[key][catKey] = (buckets[key][catKey] || 0) + 1;
    void cat;
  });
  return order.map(k => buckets[k]);
}
